import React, { useMemo, useState } from 'react';
import {
  UserGroupIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  TrophyIcon,
  ArrowsPointingOutIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface Player {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  resident_club: string;
  handicap: number;
  category: string;
  status: string;
  created_at: string;
  country: string;
  tournament_id: string;
  registration_status: { personalInfo: boolean; payment: boolean; verification: boolean };
  payment_proof_url?: string;
  shirt_size?: string;
  registration_number?: string;
  gender?: string;
}

interface MobilePlayerAnalyticsProps {
  players: Player[];
}

const BAR_COLORS = [
  '#2563eb', '#16a34a', '#d97706', '#dc2626',
  '#0891b2', '#65a30d', '#ea580c', '#be185d',
];

interface ExpandModalProps {
  title: string;
  icon: React.ElementType;
  onClose: () => void;
  children: React.ReactNode;
}

function ExpandModal({ title, icon: Icon, onClose, children }: ExpandModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-100 shadow-sm">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-golf-orange" />
          <h2 className="text-sm font-semibold text-golf-gray">{title}</h2>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {children}
      </div>
    </div>
  );
}

interface SectionCardProps {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  onExpand?: () => void;
}

function SectionCard({ title, icon: Icon, children, onExpand }: SectionCardProps) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-golf-orange" />
          <h3 className="text-sm font-semibold text-golf-gray">{title}</h3>
        </div>
        {onExpand && (
          <button
            onClick={onExpand}
            className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
          >
            <ArrowsPointingOutIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function BarRow({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs text-gray-600 truncate max-w-[65%]">{label}</span>
        <span className="text-xs font-semibold text-gray-700">{value} <span className="text-gray-400 font-normal">({pct}%)</span></span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function KpiGrid({ items }: { items: { label: string; value: string | number; sub: string }[] }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {items.map(item => (
        <div key={item.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs text-gray-500">{item.label}</p>
          <p className="text-xl font-bold text-golf-gray mt-0.5">{item.value}</p>
          <p className="text-xs text-gray-400 mt-0.5">{item.sub}</p>
        </div>
      ))}
    </div>
  );
}

type ModalKey = 'country' | 'club' | 'gender' | 'category' | 'status' | null;

export default function MobilePlayerAnalytics({ players }: MobilePlayerAnalyticsProps) {
  const total = players.length;
  const [expandedModal, setExpandedModal] = useState<ModalKey>(null);

  const avgHandicap = useMemo(() => {
    if (!total) return '—';
    return (players.reduce((s, p) => s + (p.handicap || 0), 0) / total).toFixed(1);
  }, [players, total]);

  const lowestHcp = useMemo(() => (total ? Math.min(...players.map(p => p.handicap || 0)) : '—'), [players, total]);
  const highestHcp = useMemo(() => (total ? Math.max(...players.map(p => p.handicap || 0)) : '—'), [players, total]);
  const countries = useMemo(() => new Set(players.map(p => p.country).filter(Boolean)).size, [players]);
  const clubs = useMemo(() => new Set(players.map(p => p.resident_club).filter(Boolean)).size, [players]);
  const paid = useMemo(() => players.filter(p => p.registration_status?.payment).length, [players]);

  const genderBreakdown = useMemo(() => {
    const c: Record<string, number> = {};
    players.forEach(p => {
      const g = p.gender ? p.gender.charAt(0).toUpperCase() + p.gender.slice(1).toLowerCase() : 'Not Specified';
      c[g] = (c[g] || 0) + 1;
    });
    return Object.entries(c).map(([name, value]) => ({ name, value }));
  }, [players]);

  const categoryBreakdown = useMemo(() => {
    const c: Record<string, number> = {};
    players.forEach(p => { const k = p.category || 'Unknown'; c[k] = (c[k] || 0) + 1; });
    return Object.entries(c).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [players]);

  const countryBreakdown = useMemo(() => {
    const c: Record<string, number> = {};
    const labels: Record<string, string> = {};
    players.forEach(p => {
      const raw = (p.country || 'Unknown').trim().replace(/\s+/g, ' ');
      const key = raw.toLowerCase();
      c[key] = (c[key] || 0) + 1;
      if (!labels[key]) labels[key] = raw;
    });
    return Object.entries(c).map(([key, value]) => ({ name: labels[key], value })).sort((a, b) => b.value - a.value);
  }, [players]);

  const clubBreakdown = useMemo(() => {
    const c: Record<string, number> = {};
    const labels: Record<string, string> = {};
    players.forEach(p => {
      const raw = (p.resident_club || 'Unknown').trim().replace(/\s+/g, ' ');
      const key = raw.toLowerCase();
      c[key] = (c[key] || 0) + 1;
      if (!labels[key]) labels[key] = raw;
    });
    return Object.entries(c).map(([key, value]) => ({ name: labels[key], value })).sort((a, b) => b.value - a.value);
  }, [players]);

  const statusBreakdown = useMemo(() => {
    const c: Record<string, number> = {};
    players.forEach(p => { const k = p.status ? p.status.charAt(0).toUpperCase() + p.status.slice(1).toLowerCase() : 'Unknown'; c[k] = (c[k] || 0) + 1; });
    return Object.entries(c).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [players]);

  const isMale = (p: Player) => { const g = (p.gender || '').toLowerCase(); return g === 'male' || g === 'm'; };
  const isFemale = (p: Player) => { const g = (p.gender || '').toLowerCase(); return g === 'female' || g === 'f' || g === 'lady' || g === 'ladies'; };

  const hcpByGender = useMemo(() => {
    const males = players.filter(isMale);
    const females = players.filter(isFemale);
    return [
      { name: 'Male 0–18', value: males.filter(p => p.handicap >= 0 && p.handicap <= 18).length, color: '#2563eb' },
      { name: 'Male 19–28', value: males.filter(p => p.handicap >= 19 && p.handicap <= 28).length, color: '#60a5fa' },
      { name: 'Ladies 0–18', value: females.filter(p => p.handicap >= 0 && p.handicap <= 18).length, color: '#16a34a' },
      { name: 'Ladies 19–36', value: females.filter(p => p.handicap >= 19 && p.handicap <= 36).length, color: '#4ade80' },
    ];
  }, [players]);

  const maxHcp = useMemo(() => Math.max(...hcpByGender.map(b => b.value), 1), [hcpByGender]);

  const shirtSizes = useMemo(() => {
    const order = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
    const isMale = (p: Player) => { const g = (p.gender || '').toLowerCase(); return g === 'male' || g === 'm'; };
    const isFemale = (p: Player) => { const g = (p.gender || '').toLowerCase(); return g === 'female' || g === 'f' || g === 'lady' || g === 'ladies'; };
    const male: Record<string, number> = {};
    const female: Record<string, number> = {};
    players.forEach(p => {
      const s = p.shirt_size?.toUpperCase();
      if (!s) return;
      if (isFemale(p)) female[s] = (female[s] || 0) + 1;
      else { male[s] = (male[s] || 0) + 1; }
    });
    const allSizes = new Set([...Object.keys(male), ...Object.keys(female)]);
    return order.filter(s => allSizes.has(s)).map(s => ({
      name: s,
      male: male[s] || 0,
      female: female[s] || 0,
    }));
  }, [players]);

  if (!total) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-400 px-4">
        <UserGroupIcon className="w-12 h-12 mb-3 text-gray-300" />
        <p className="text-sm font-medium">No player data available</p>
        <p className="text-xs mt-1">Register players to see analytics</p>
      </div>
    );
  }

  const renderFullList = (items: { name: string; value: number }[], colorOffset = 0) => (
    <div className="space-y-0">
      {items.map((item, i) => (
        <BarRow key={item.name} label={item.name} value={item.value} total={total} color={BAR_COLORS[(i + colorOffset) % BAR_COLORS.length]} />
      ))}
    </div>
  );

  return (
    <>
      {expandedModal === 'country' && (
        <ExpandModal title="Country Breakdown" icon={GlobeAltIcon} onClose={() => setExpandedModal(null)}>
          {renderFullList(countryBreakdown)}
        </ExpandModal>
      )}
      {expandedModal === 'club' && (
        <ExpandModal title="Resident Club Breakdown" icon={BuildingOfficeIcon} onClose={() => setExpandedModal(null)}>
          {renderFullList(clubBreakdown)}
        </ExpandModal>
      )}
      {expandedModal === 'gender' && (
        <ExpandModal title="Gender Breakdown" icon={UserGroupIcon} onClose={() => setExpandedModal(null)}>
          {renderFullList(genderBreakdown)}
        </ExpandModal>
      )}
      {expandedModal === 'category' && (
        <ExpandModal title="Category Breakdown" icon={TrophyIcon} onClose={() => setExpandedModal(null)}>
          {renderFullList(categoryBreakdown, 2)}
        </ExpandModal>
      )}
      {expandedModal === 'status' && (
        <ExpandModal title="Registration Status" icon={TrophyIcon} onClose={() => setExpandedModal(null)}>
          {renderFullList(statusBreakdown, 3)}
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-2">
            {[
              { label: 'Info', count: players.filter(p => p.registration_status?.personalInfo).length },
              { label: 'Payment', count: players.filter(p => p.registration_status?.payment).length },
              { label: 'Verified', count: players.filter(p => p.registration_status?.verification).length },
            ].map(step => (
              <div key={step.label} className="bg-gray-50 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-golf-gray">{step.count}</p>
                <p className="text-xs text-gray-500">{step.label}</p>
                <p className="text-xs text-gray-400">{total > 0 ? Math.round((step.count / total) * 100) : 0}%</p>
              </div>
            ))}
          </div>
        </ExpandModal>
      )}

      <div className="px-4 pb-6 space-y-4">
        {/* KPIs */}
        <KpiGrid items={[
          { label: 'Total Players', value: total, sub: `${paid} paid` },
          { label: 'Avg Handicap', value: avgHandicap, sub: `${lowestHcp} – ${highestHcp}` },
          { label: 'Countries', value: countries, sub: 'represented' },
          { label: 'Clubs', value: clubs, sub: 'resident clubs' },
        ]} />

        {/* Payment Status */}
        <SectionCard title="Payment Status" icon={TrophyIcon}>
          <BarRow label="Paid" value={paid} total={total} color="#16a34a" />
          <BarRow label="Pending" value={total - paid} total={total} color="#d97706" />
          <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-3 gap-2">
            {[
              { label: 'Info', count: players.filter(p => p.registration_status?.personalInfo).length },
              { label: 'Payment', count: players.filter(p => p.registration_status?.payment).length },
              { label: 'Verified', count: players.filter(p => p.registration_status?.verification).length },
            ].map(step => (
              <div key={step.label} className="bg-gray-50 rounded-xl p-2.5 text-center">
                <p className="text-base font-bold text-golf-gray">{step.count}</p>
                <p className="text-xs text-gray-500">{step.label}</p>
                <p className="text-xs text-gray-400">{total > 0 ? Math.round((step.count / total) * 100) : 0}%</p>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Gender */}
        <SectionCard title="Gender Breakdown" icon={UserGroupIcon} onExpand={() => setExpandedModal('gender')}>
          <div className="max-h-40 overflow-hidden">
            {genderBreakdown.slice(0, 4).map((item, i) => (
              <BarRow key={item.name} label={item.name} value={item.value} total={total} color={BAR_COLORS[i % BAR_COLORS.length]} />
            ))}
          </div>
          {genderBreakdown.length > 4 && (
            <button onClick={() => setExpandedModal('gender')} className="mt-2 text-xs text-golf-orange font-medium">
              +{genderBreakdown.length - 4} more
            </button>
          )}
        </SectionCard>

        {/* Category */}
        <SectionCard title="Category Breakdown" icon={TrophyIcon} onExpand={() => setExpandedModal('category')}>
          <div className="max-h-40 overflow-hidden">
            {categoryBreakdown.slice(0, 4).map((item, i) => (
              <BarRow key={item.name} label={item.name} value={item.value} total={total} color={BAR_COLORS[(i + 2) % BAR_COLORS.length]} />
            ))}
          </div>
          {categoryBreakdown.length > 4 && (
            <button onClick={() => setExpandedModal('category')} className="mt-2 text-xs text-golf-orange font-medium">
              +{categoryBreakdown.length - 4} more
            </button>
          )}
        </SectionCard>

        {/* Handicap Distribution by Gender */}
        <SectionCard title="Handicap Distribution by Gender" icon={TrophyIcon}>
          <div className="flex items-end gap-2 h-32 mb-3">
            {hcpByGender.map(b => (
              <div key={b.name} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs font-semibold" style={{ color: b.color }}>{b.value > 0 ? b.value : ''}</span>
                <div
                  className="w-full rounded-t-md transition-all duration-500"
                  style={{
                    height: `${maxHcp > 0 ? Math.max(Math.round((b.value / maxHcp) * 80), b.value > 0 ? 4 : 0) : 0}px`,
                    backgroundColor: b.color,
                    minHeight: b.value > 0 ? '4px' : '0',
                  }}
                />
                <span className="text-gray-400 leading-tight text-center" style={{ fontSize: '8px' }}>{b.name}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-1.5 pt-2 border-t border-gray-100">
            {hcpByGender.map(b => (
              <div key={b.name} className="flex items-center justify-between bg-gray-50 rounded-lg px-2.5 py-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: b.color }} />
                  <span className="text-xs text-gray-600 truncate">{b.name}</span>
                </div>
                <span className="text-xs font-bold text-gray-800 ml-1">{b.value}</span>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Country */}
        <SectionCard title="Country Breakdown" icon={GlobeAltIcon} onExpand={() => setExpandedModal('country')}>
          <div className="max-h-40 overflow-hidden">
            {countryBreakdown.slice(0, 4).map((item, i) => (
              <BarRow key={item.name} label={item.name} value={item.value} total={total} color={BAR_COLORS[i % BAR_COLORS.length]} />
            ))}
          </div>
          {countryBreakdown.length > 4 && (
            <button onClick={() => setExpandedModal('country')} className="mt-2 text-xs text-golf-orange font-medium">
              +{countryBreakdown.length - 4} more
            </button>
          )}
        </SectionCard>

        {/* Club */}
        <SectionCard title="Resident Club Breakdown" icon={BuildingOfficeIcon} onExpand={() => setExpandedModal('club')}>
          <div className="max-h-40 overflow-hidden">
            {clubBreakdown.slice(0, 4).map((item, i) => (
              <BarRow key={item.name} label={item.name} value={item.value} total={total} color={BAR_COLORS[i % BAR_COLORS.length]} />
            ))}
          </div>
          {clubBreakdown.length > 4 && (
            <button onClick={() => setExpandedModal('club')} className="mt-2 text-xs text-golf-orange font-medium">
              +{clubBreakdown.length - 4} more
            </button>
          )}
        </SectionCard>

        {/* Shirt Sizes */}
        <SectionCard title="Shirt Size Breakdown" icon={UserGroupIcon}>
          <div className="flex gap-3 mb-2">
            <span className="flex items-center gap-1 text-xs text-gray-500"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: '#2563eb' }} />Male</span>
            <span className="flex items-center gap-1 text-xs text-gray-500"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: '#e11d48' }} />Female</span>
          </div>
          <div className="flex items-end gap-1.5" style={{ height: '96px' }}>
            {shirtSizes.map(s => {
              const maxVal = Math.max(...shirtSizes.flatMap(x => [x.male, x.female]), 1);
              const maleH = Math.round((s.male / maxVal) * 56);
              const femaleH = Math.round((s.female / maxVal) * 56);
              return (
                <div key={s.name} className="flex-1 flex flex-col items-center gap-0.5">
                  <div className="w-full flex items-end gap-0.5" style={{ height: '72px' }}>
                    <div className="flex-1 flex flex-col items-center justify-end">
                      {s.male > 0 && <span className="text-[9px] text-blue-600 font-semibold leading-none mb-0.5">{s.male}</span>}
                      <div
                        className="w-full rounded-t-sm"
                        style={{ height: `${maleH}px`, backgroundColor: '#2563eb', minHeight: s.male ? '4px' : '0' }}
                      />
                    </div>
                    <div className="flex-1 flex flex-col items-center justify-end">
                      {s.female > 0 && <span className="text-[9px] text-rose-600 font-semibold leading-none mb-0.5">{s.female}</span>}
                      <div
                        className="w-full rounded-t-sm"
                        style={{ height: `${femaleH}px`, backgroundColor: '#e11d48', minHeight: s.female ? '4px' : '0' }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-gray-400">{s.name}</span>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>
    </>
  );
}
